import { postJson } from "@/domain/common/client/ApiClient"
import { toYearDayTime } from "@/domain/common/utils/DateUtils"
import { statPresentation } from "@/domain/admin/model/AdminDashboard"
import { entryStatusLabel } from "@/domain/entry/model/Entry"
import { sportLabel } from "@/domain/fight/model/Fight"
import {
  AdminDashboardGetSummaryRequestPayload,
  AdminDashboardGetSummaryResponsePayload,
} from "@bracket/idl/main/admin/payload/AdminDashboardPayloadPt"
import type {
  AdminDashboardRecentEntryPayload,
  AdminDashboardStatPayload,
} from "@bracket/idl/main/admin/payload/AdminDashboardPayloadPt"
import type {
  AdminDashboardStat,
  AdminDashboardSummary,
  AdminRecentEntry,
} from "@/domain/admin/model/AdminDashboard"

function toStat(payload: AdminDashboardStatPayload): AdminDashboardStat {
  const presentation = statPresentation(payload.key)

  return {
    key: payload.key,
    label: presentation.label,
    value: payload.value.toLocaleString("ko-KR"),
    detail: presentation.detail,
    icon: presentation.icon,
    tone: presentation.tone,
  }
}

function toRecentEntry(
  payload: AdminDashboardRecentEntryPayload,
): AdminRecentEntry {
  const restPlayerCount = Math.max(0, payload.playerCount - 1)
  const playerName = restPlayerCount
    ? `${payload.playerName} 외 ${restPlayerCount}명`
    : payload.playerName

  return {
    entryNo: payload.entryNo,
    playerName,
    gymName: payload.gymName,
    category: sportLabel(payload.sport),
    appliedAtText: toYearDayTime(payload.appliedAt),
    statusLabel: entryStatusLabel(payload.status),
  }
}

/**
 * 관리자 대시보드 클라이언트.
 *
 * 경로는 fight-bracket-server 의 `AdminDashboardRouter` 와 1:1 대응한다.
 * 대회를 지정하지 않으면 접수 중 · 진행 중 대회를 합산한 값이 내려온다.
 */
const AdminDashboardClient = {
  async getSummary(tournamentId = ""): Promise<AdminDashboardSummary> {
    const request = AdminDashboardGetSummaryRequestPayload.create({
      tournamentId,
    })

    const json = await postJson<unknown>(
      "/bo/dashboard/get-summary",
      AdminDashboardGetSummaryRequestPayload.toJSON(request),
    )
    const response = AdminDashboardGetSummaryResponsePayload.fromJSON(json)

    return {
      managerName: response.managerName,
      stats: response.stat.map(toStat),
      recentEntries: response.recentEntry.map(toRecentEntry),
    }
  },
}

export { AdminDashboardClient, toRecentEntry, toStat }
